import { createSelector } from 'reselect';
import {
  getOneXpExperiments,
  hasOneXpRequestFinished
} from 'src/universal/redux/oneXp';
import { getExperimentalPageData } from 'src/universal/redux/page';
import { getVacPageData } from 'page_modules/business-vac/redux/mainPageData';
import { getActiveFilter, getFilterContent } from 'src/universal/redux/cardsFilter';
import { hasPznDataArrived, getPznOfferList } from 'src/universal/redux/pznData';
import { gcsPrefix } from 'page_modules/business-vac/components/PillboxFilter/redux';
import { getPageCardsData } from 'src/universal/redux/page';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import { getPageData } from 'src/universal/redux/page';
import { isCardMember } from 'src/universal/redux/userType';
import { getMemberCardShortNames } from 'src/universal/redux/cardMemberInfo';

// to whoever cleans up this experiment
// if this experiment fails, revert all changes in these two PRs:
// https://github.aexp.com/amex-eng/acq-shop-commercial/pull/4224/files
// https://github.aexp.com/amex-eng/eapply-cardshop-backend/pull/8033/files
// if this experiment wins, most of these functions can be deleted (anyone of them that says analagous to...)
// however, getRankedCards and getG174Cards will have to be integrated into the BAU redux
// any variables that were renamed for G174 will have to be reverted
// the BE json will have to be updated with the test data here: app/data/icm/GQL/Business/Pages/businessCategoryPageData.json
// most of the logic will work as is if the BE json is updated - it's only the above two functions that need to be kept
// also talk with product if they want `/recommended` to be a new URL for this filter

export const getG174Data = createSelector(
  [getExperimentalPageData],
  ({ g174FilterData }) => g174FilterData
);

// checks to see if user is a card member, and then also checks their cardmember cards
// to see if they're eligible. only card members with premium cards
// will see the recommended cards pillbox filter
export const eligibleForG174Experiment = createSelector(
  [isCardMember, getMemberCardShortNames, getG174Data],
  (isCM, cmCards, g174Data) => {
    let isEligible = false;
    if (isCM && cmCards && g174Data?.eligibleCards) {
      isEligible = g174Data?.eligibleCards.some(eachEligibleCard =>
        cmCards.includes(eachEligibleCard)
      );
    }
    return isEligible;
  }
);

export const isG174Experiment = createSelector(
  [getOneXpExperiments, hasOneXpRequestFinished, eligibleForG174Experiment],
  (oneXpExperiments, oneXpDecided, isEligible) => {
    return (
      oneXpExperiments['GCS-174-CM-Recommended-Filter']?.dimensions[0]?.variant ===
        'experiment' &&
      oneXpDecided &&
      isEligible
    );
  }
);

//analagous to the getFilterData function found here:
// page_modules/business-vac/components/PillboxFilter/redux.js
export const getG174FilterData = createSelector(
  [getFilterContent(true), getVacPageData, getG174Data],
  (bauFilterData, { pillboxVacFilter = {} }, g174Data) => {
    const bauList = bauFilterData?.filters?.items || [];
    const g174List = bauList.toSpliced(1, 0, g174Data?.vacFormat);

    const pillboxOrderList = pillboxVacFilter?.order || [];
    const g174Order = pillboxOrderList.toSpliced(1, 0, 'recommended');
    const sortedList = [];
    const replicatedList = g174List.map(item => {
      const idVal = item.id;
      return {
        ...item,
        id: `${gcsPrefix}${idVal}`,
        internalId: idVal
      };
    });

    g174Order.forEach(item => {
      const targetItem = replicatedList.find(bauItem => bauItem.internalId === item);
      if (targetItem) {
        sortedList.push({ ...targetItem });
      }
    });

    const g174VacFilter = { ...pillboxVacFilter, order: g174Order };

    return {
      ...(g174VacFilter || {}),
      sortedList
    };
  }
);

export const getRankedCards = createSelector(
  [getG174Data, getMemberCardShortNames, eligibleForG174Experiment],
  (g174Data, cmCards, isEligible) => {
    let rankedCards = [];
    if (!isEligible) {
      return rankedCards;
    }
    const cardRanking = g174Data?.cardRanking;
    const getCardWeight = card => {
      if (!cardRanking[card]) {
        return '5';
      }
      return cardRanking[card];
    };

    // i have a "weighted" object in the BE that sorts this array according to their "weight"
    //  so i can find out the most premium cards in the array
    rankedCards = cmCards.toSorted((a, b) => getCardWeight(a) - getCardWeight(b));
    return rankedCards;
  }
);

export const getG174Cards = createSelector(
  [getG174Data, getRankedCards, eligibleForG174Experiment],
  (g174Data, rankedCards, isEligible) => {
    let g174Cards = [];
    if (!isEligible) {
      return g174Cards;
    }

    // most of the logic is based off the most premium card in the array of rankedCards.
    // i have an object in the BE that matches up the cardmember card with its appropriate recommended card array
    // this is what line 138 covers
    // however, there are two situations where that logic does not work:
    // where the first two cards are platinum and gold or the first two cards are blue-business-cash and blue-business-plus
    // in those two situations, product wanted a different array of recommended cards

    const key = `${rankedCards[0]}+${rankedCards[1]}`;
    if (g174Data?.cardMap[key]) {
      g174Cards = g174Data?.cardMap[key];
    } else {
      g174Cards = g174Data?.cardMap[rankedCards[0]];
    }
    return g174Cards;
  }
);

// this function gets the BAU filter content and then
// injects the g174 cards into the appropriate place
export const insertG174Data = createSelector(
  [getVacPageData, getG174Data, getG174Cards],
  (pageData, g174Data, g174Cards) => {
    const vacFilterContent = pageData?.vacFilter || {};
    let cardsArray = [];
    g174Cards.forEach(eachCard => {
      cardsArray.push(eachCard);
    });

    const g174Pill = {
      ...g174Data?.pillData,
      cards: cardsArray
    };

    const g174VacFilter = {
      ...vacFilterContent,
      content: vacFilterContent?.content?.toSpliced(1, 0, g174Pill)
    };
    return g174VacFilter;
  }
);

//analagous to the getFilterCardCountHash function found here:
// page_modules/business-vac/components/PillboxFilter/redux.js
export const getG174FilterCardCountHash = createSelector(
  [insertG174Data, hasPznDataArrived, getPznOfferList],
  (g174Data, pznArrived, offerList) => {
    const filterNameCntHash = g174Data?.content?.reduce((accum, item) => {
      const nameList = Array.isArray(item?.cards) ? item?.cards : [];
      let cnt = nameList.length;
      if (pznArrived) {
        const filteredList = nameList.filter(
          sName => !!offerList.find(offer => offer.shortName === sName)
        );
        cnt = filteredList.length;
      }
      accum[item.filter] = cnt;
      return accum;
    }, {});

    return filterNameCntHash;
  }
);

//analagous to getActiveFilterCards function found here:
//src/universal/redux/cardsFilter.js
export const getG174ActiveFilterCards = createSelector(
  [getActiveFilter, insertG174Data, hasPznDataArrived, getPznOfferList],
  (activeFilter, g174Data, pznArrived, offerList) => {
    const filters = g174Data?.content || [];
    const activeFilterContent =
      filters.filter(item => {
        return item.filter === activeFilter;
      })[0] || {};

    const cardNames = Array.isArray(activeFilterContent.cards)
      ? activeFilterContent.cards
      : [];

    if (pznArrived) {
      return cardNames.filter(
        name => !!offerList.find(offer => offer.shortName === name)
      );
    }
    return cardNames;
  }
);

//analagous to the getCardListOrderData found here:
//page_modules/business-vac/components/CardListCarousel/redux.js
export const getG174CardListOrderData = createSelector(
  [getPageData, getG174ActiveFilterCards, getPageCardsData],
  ({ cardListCarousel }, cardNameList, allCards) => {
    const dtm = (cardListCarousel?.dtmEvents || []).find(
      dtm => dtm.key === 'horizontalTileClick'
    );

    const cardList = allCards.reduce((accumulator, card) => {
      const shouldShow = cardNameList.indexOf(card.shortName) > -1;
      if (shouldShow) {
        accumulator.push({
          cardTitle: card.cardTitle,
          cardArtUrl: card.appAssets?.cardArt,
          cardArtAltText: card.cardArtAltText,
          learnMoreUrl: card.learnMore?.url,
          shortName: card.shortName,
          unitDtm: {
            ...(dtm || {}),
            eventAsset: replaceStringWithVars({
              varsObject: { product: card.dtmProductName },
              string: dtm.eventAsset
            }),
            tag: replaceStringWithVars({
              varsObject: { product: card.dtmProductName },
              string: dtm.tag
            })
          }
        });
      }
      return accumulator;
    }, []);

    const baseDtm = (cardListCarousel?.dtmEvents || []).find(
      dtm => dtm.key === 'horizontalTileImpression'
    );
    const cardImpressionEventMap = (allCards || []).reduce((accumulator, card) => {
      accumulator[card.shortName] = {
        ...baseDtm,
        eventAsset: replaceStringWithVars({
          varsObject: { product: card.dtmProductName },
          string: baseDtm.eventAsset
        })
      };
      return accumulator;
    }, {});
    return {
      cardListCarousel,
      cardList,
      cardImpressionEventMap
    };
  }
);

//analagous to the getActiveFilterLabel function found here:
//src/universal/redux/cardsFilter.js
export const getG174ActiveFilterLabel = createSelector(
  [getActiveFilter, insertG174Data],
  (activeFilter, g174Data) => {
    const filterList = g174Data?.content || [];
    return (filterList.find(item => item.filter === activeFilter) || {}).header;
  }
);
